import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Zap,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  ChevronRight,
  Lightbulb,
  Activity,
  AlertCircle,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { insightsApi } from '@/api/insights';
import type {
  InsightSummary,
  AnomalyDetectionResponse,
  RecommendationsResponse,
} from '@/types/data';

type TabType = 'summary' | 'anomalies' | 'recommendations';

export default function Insights() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [periodDays, setPeriodDays] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // データ
  const [summary, setSummary] = useState<InsightSummary | null>(null);
  const [anomalies, setAnomalies] = useState<AnomalyDetectionResponse | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationsResponse | null>(null);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // サマリー取得
  const fetchSummary = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const data = await insightsApi.getSummary({
        company_id: selectedCompanyId,
        period_days: periodDays,
      });
      setSummary(data);
    } catch (err) {
      console.error('サマリー取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, periodDays]);

  // 異常検知取得
  const fetchAnomalies = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const data = await insightsApi.detectAnomalies({
        company_id: selectedCompanyId,
        baseline_days: periodDays,
        analysis_days: 7,
      });
      setAnomalies(data);
    } catch (err) {
      console.error('異常検知エラー:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, periodDays]);

  // 改善提案取得
  const fetchRecommendations = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const data = await insightsApi.getRecommendations({
        company_id: selectedCompanyId,
        period_days: periodDays,
      });
      setRecommendations(data);
    } catch (err) {
      console.error('改善提案取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, periodDays]);

  // タブ切り替え時にデータ取得
  useEffect(() => {
    if (!selectedCompanyId) return;
    switch (activeTab) {
      case 'summary':
        fetchSummary();
        break;
      case 'anomalies':
        fetchAnomalies();
        break;
      case 'recommendations':
        fetchRecommendations();
        break;
    }
  }, [activeTab, selectedCompanyId, periodDays, fetchSummary, fetchAnomalies, fetchRecommendations]);

  // インサイト生成
  const handleGenerateInsights = async () => {
    if (!selectedCompanyId) return;
    setIsGenerating(true);
    try {
      const result = await insightsApi.generateInsights(selectedCompanyId);
      alert(result.message);
      // サマリーを再取得
      fetchSummary();
    } catch (err) {
      console.error('インサイト生成エラー:', err);
      alert('インサイトの生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  };

  const tabs = [
    { id: 'summary' as const, label: 'サマリー', icon: Activity },
    { id: 'anomalies' as const, label: '異常検知', icon: AlertTriangle },
    { id: 'recommendations' as const, label: '改善提案', icon: Lightbulb },
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <Zap className="w-4 h-4 text-yellow-500" />;
      case 'acknowledged':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'in_progress':
        return <RefreshCw className="w-4 h-4 text-purple-500" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'dismissed':
        return <XCircle className="w-4 h-4 text-gray-400" />;
      default:
        return null;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'anomaly':
        return <AlertTriangle className="w-4 h-4" />;
      case 'opportunity':
        return <TrendingUp className="w-4 h-4" />;
      case 'risk':
        return <TrendingDown className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AIインサイト</h1>
          <p className="text-gray-500 mt-1">
            異常検知と改善提案を自動生成
          </p>
        </div>

        <div className="flex items-center gap-4">
          {accessibleCompanies.length > 1 && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">法人:</span>
              <select
                value={selectedCompanyId || ''}
                onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary focus:border-primary"
              >
                {accessibleCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.display_name || company.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">期間:</span>
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary focus:border-primary"
            >
              <option value={7}>過去7日間</option>
              <option value={14}>過去14日間</option>
              <option value={30}>過去30日間</option>
              <option value={60}>過去60日間</option>
            </select>
          </label>
          <button
            onClick={handleGenerateInsights}
            disabled={isGenerating || !selectedCompanyId}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
            インサイト生成
          </button>
        </div>
      </div>

      {/* 法人未選択時のメッセージ */}
      {!selectedCompanyId && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>法人を選択してください</p>
          </div>
        </div>
      )}

      {/* タブ */}
      {selectedCompanyId && (
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4 px-4" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {/* サマリー */}
                {activeTab === 'summary' && summary && (
                  <div className="space-y-6">
                    {/* 概要カード */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-blue-600 font-medium">総インサイト数</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {summary.total_insights}
                        </p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4">
                        <p className="text-sm text-red-600 font-medium">重要度: 高</p>
                        <p className="text-2xl font-bold text-red-700">
                          {(summary.by_severity?.['critical'] || 0) + (summary.by_severity?.['high'] || 0)}
                        </p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <p className="text-sm text-yellow-600 font-medium">未対応</p>
                        <p className="text-2xl font-bold text-yellow-700">
                          {(summary.by_status?.['new'] || 0) + (summary.by_status?.['acknowledged'] || 0)}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-green-600 font-medium">解決済み</p>
                        <p className="text-2xl font-bold text-green-700">
                          {summary.by_status?.['resolved'] || 0}
                        </p>
                      </div>
                    </div>

                    {/* タイプ別・重要度別 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* タイプ別 */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4">タイプ別</h3>
                        <div className="space-y-2">
                          {Object.entries(summary.by_type ?? {}).map(([type, count]) => (
                            <div key={type} className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-sm text-gray-600">
                                {getTypeIcon(type)}
                                {type}
                              </span>
                              <span className="font-semibold">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 重要度別 */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4">重要度別</h3>
                        <div className="space-y-2">
                          {['critical', 'high', 'medium', 'low'].map((severity) => (
                            <div key={severity} className="flex items-center justify-between">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(
                                  severity
                                )}`}
                              >
                                {severity}
                              </span>
                              <span className="font-semibold">
                                {summary.by_severity?.[severity] || 0}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 最新インサイト */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">最新インサイト</h3>
                      {(summary.recent_insights?.length ?? 0) > 0 ? (
                        <div className="space-y-3">
                          {(summary.recent_insights ?? []).map((insight) => (
                            <div
                              key={insight.id}
                              className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200"
                            >
                              <div className="flex items-center gap-3">
                                {getStatusIcon(insight.status)}
                                <div>
                                  <p className="font-medium text-gray-900">{insight.title}</p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(insight.detected_at).toLocaleString('ja-JP')}
                                  </p>
                                </div>
                              </div>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(
                                  insight.severity
                                )}`}
                              >
                                {insight.severity}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">インサイトがありません</p>
                      )}
                    </div>
                  </div>
                )}

                {/* 異常検知 */}
                {activeTab === 'anomalies' && anomalies && (
                  <div className="space-y-6">
                    {/* 統計情報 */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-blue-600 font-medium">ベースライン平均</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {(anomalies.baseline_stats?.mean ?? 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-purple-600 font-medium">標準偏差</p>
                        <p className="text-2xl font-bold text-purple-700">
                          {(anomalies.baseline_stats?.std ?? 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <p className="text-sm text-yellow-600 font-medium">検出異常数</p>
                        <p className="text-2xl font-bold text-yellow-700">
                          {anomalies.total_anomalies ?? 0}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-green-600 font-medium">分析期間</p>
                        <p className="text-lg font-bold text-green-700">
                          {anomalies.analysis_period?.start_date ?? '-'} ～
                        </p>
                      </div>
                    </div>

                    {/* 異常リスト */}
                    {(anomalies.anomalies?.length ?? 0) > 0 ? (
                      <div className="space-y-4">
                        {(anomalies.anomalies ?? []).map((anomaly, index) => (
                          <div
                            key={index}
                            className={`rounded-lg p-4 border ${
                              anomaly.anomaly_type === 'spike'
                                ? 'bg-green-50 border-green-200'
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {anomaly.anomaly_type === 'spike' ? (
                                  <TrendingUp className="w-5 h-5 text-green-600" />
                                ) : (
                                  <TrendingDown className="w-5 h-5 text-red-600" />
                                )}
                                <span className="font-semibold text-gray-900">
                                  {anomaly.date}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(
                                    anomaly.severity
                                  )}`}
                                >
                                  {anomaly.severity}
                                </span>
                              </div>
                              <span className="text-sm text-gray-500">
                                Z-スコア: {(anomaly.z_score ?? 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">実測値</p>
                                <p className="font-semibold">{(anomaly.actual_value ?? 0).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">期待値</p>
                                <p className="font-semibold">{(anomaly.expected_value ?? 0).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">乖離率</p>
                                <p
                                  className={`font-semibold ${
                                    (anomaly.deviation_percent ?? 0) > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}
                                >
                                  {(anomaly.deviation_percent ?? 0) > 0 ? '+' : ''}
                                  {(anomaly.deviation_percent ?? 0).toFixed(1)}%
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>異常は検出されませんでした</p>
                      </div>
                    )}
                  </div>
                )}

                {/* 改善提案 */}
                {activeTab === 'recommendations' && recommendations && (
                  <div className="space-y-4">
                    {(recommendations.recommendations?.length ?? 0) > 0 ? (
                      (recommendations.recommendations ?? []).map((rec) => (
                        <div
                          key={rec.id || rec.title}
                          className="bg-white rounded-lg border border-gray-200 p-4"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                              <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">優先度:</span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  (rec.priority_score ?? 0) >= 70
                                    ? 'bg-red-100 text-red-700'
                                    : (rec.priority_score ?? 0) >= 50
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {(rec.priority_score ?? 0).toFixed(0)}
                              </span>
                            </div>
                          </div>

                          {rec.expected_impact && (
                            <p className="text-sm text-gray-500 mb-3">
                              想定効果: {rec.expected_impact}
                            </p>
                          )}

                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">推奨アクション:</p>
                            {(rec.actions ?? []).map((action, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <ChevronRight className="w-4 h-4 text-primary" />
                                <span>{action.action}</span>
                                <span
                                  className={`px-2 py-0.5 rounded text-xs ${
                                    action.priority === 'high'
                                      ? 'bg-red-100 text-red-700'
                                      : action.priority === 'medium'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {action.priority}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <Lightbulb className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>改善提案がありません</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
